import { gql, useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import React from "react";
import { toast } from "react-toastify";
import { useMount, useTitle } from "react-use";
import Button from "../../../components/Button";
import { FormField } from "../../../components/forms/shared";
import TextInput from "../../../components/forms/TextInput";
import QueryComponent from "../../../components/QueryComponent";
import Select from "../../../components/Select";
import Spinner from "../../../components/Spinner";
import { STATES } from "../../../constants/states";
const Contact = () => {
  useTitle("Settings - Contact Information | Due");

  const [updateSelf] = useMutation(gql`
    mutation UpdateSelf($input: SelfMutationInput!) {
      self(input: $input) {
        firstName
      }
    }
  `);

  const [updateProfile] = useMutation(gql`
    mutation UpdateProfile($input: ProfileMutationInput!) {
      profile(input: $input) {
        addressLine1
      }
    }
  `);

  useMount(() => {
    document.getElementById("app-container")?.scrollTo({ top: 0 });
  });

  return (
    <div className="p-6 mb-16">
      <h2 className="text-lg leading-6 font-medium text-gray-900">
        Contact Information
      </h2>
      <p className="mt-1 text-sm text-gray-500 mb-6">
        This information will be use to contact you.
      </p>

      <QueryComponent
        query={{
          query: gql`
            {
              self {
                firstName
                middleName
                lastName
                phoneNumber
              }
              profile {
                addressLine1
                addressLine2
                city
                state
                zipCode
                country
              }
            }
          `,
        }}
        loader={() => (
          <div className="w-full h-80 flex items-center justify-center">
            <Spinner />
          </div>
        )}
        error={() => (
          <div className="h-80 flex items-center justify-center">
            <div className="text-red-500">
              Failed to load user data. Please try again later.
            </div>
          </div>
        )}
        options={{ onLoadRefetch: true }}
      >
        {(data, refetch) => {
          const { firstName, middleName, lastName, phoneNumber } =
            data && data.self
              ? data.self
              : {
                  firstName: "",
                  middleName: "",
                  lastName: "",
                  phoneNumber: "",
                };

          const { addressLine1, addressLine2, city, state, zipCode } =
            data && data.profile
              ? data.profile
              : {
                  addressLine1: "",
                  addressLine2: "",
                  city: "",
                  state: "",
                  zipCode: "",
                };
          return (
            <Formik
              validateOnChange={false}
              validateOnBlur={true}
              enableReinitialize
              initialValues={{
                firstName,
                middleName,
                lastName,
                addressLine1,
                addressLine2,
                city,
                state,
                zipCode,
                country: "United States",
                phoneNumber,
              }}
              onSubmit={({
                firstName,
                lastName,
                middleName,
                addressLine1,
                addressLine2,
                city,
                state,
                zipCode,
                country,
                phoneNumber,
              }) => {
                Promise.all([
                  updateSelf({
                    variables: {
                      input: { firstName, lastName, middleName, phoneNumber },
                    },
                  }),
                  updateProfile({
                    variables: {
                      input: {
                        addressLine1,
                        addressLine2,
                        city,
                        state,
                        zipCode,
                        country,
                      },
                    },
                  }),
                ])
                  .then(() => {
                    toast("Contact information saved.");
                    refetch();
                  })
                  .catch(() => {
                    toast(
                      "Contact information failed to saved. Please try again later."
                    );
                  });
              }}
            >
              {({ values, errors, setFieldValue }) => (
                <Form>
                  <div className="flex">
                    <FormField
                      className="w-8/12"
                      id="firstName"
                      label="First name"
                      error={errors.firstName}
                    >
                      <TextInput
                        id="firstName"
                        name="firstName"
                        value={values.firstName}
                        onChange={(e) => {
                          setFieldValue("firstName", e.target.value);
                        }}
                      />
                    </FormField>
                    <FormField
                      className="ml-2 w-4/12"
                      id="middleName"
                      label="Middle name"
                      error={errors.middleName}
                    >
                      <TextInput
                        id="middleName"
                        name="middleName"
                        value={values.middleName}
                        onChange={(e) => {
                          setFieldValue("middleName", e.target.value);
                        }}
                      />
                    </FormField>
                  </div>
                  <FormField
                    id="lastName"
                    label="Last name"
                    error={errors.lastName}
                  >
                    <TextInput
                      id="lastName"
                      name="lastName"
                      value={values.lastName}
                      onChange={(e) => {
                        setFieldValue("lastName", e.target.value);
                      }}
                    />
                  </FormField>
                  <FormField
                    id="addressLine1"
                    label="Address Line 1"
                    error={errors.addressLine1}
                  >
                    <TextInput
                      id="addressLine1"
                      name="addressLine1"
                      value={values.addressLine1}
                      onChange={(e) => {
                        setFieldValue("addressLine1", e.target.value);
                      }}
                    />
                  </FormField>
                  <FormField
                    id="addressLine2"
                    label="Address Line 2"
                    error={errors.addressLine2}
                  >
                    <TextInput
                      id="addressLine2"
                      name="addressLine2"
                      value={values.addressLine2}
                      onChange={(e) => {
                        setFieldValue("addressLine2", e.target.value);
                      }}
                    />
                  </FormField>
                  <div className="flex">
                    <FormField
                      className="w-6/12 mr-1"
                      id="city"
                      label="City"
                      error={errors.city}
                    >
                      <TextInput
                        id="city"
                        name="city"
                        value={values.city}
                        onChange={(e) => {
                          setFieldValue("city", e.target.value);
                        }}
                      />
                    </FormField>
                    <FormField
                      className="w-6/12 ml-1"
                      id="state"
                      label="State"
                      error={errors.state}
                    >
                      <Select
                        placeholder="Select state..."
                        valueKey={values.state}
                        options={STATES}
                        onChange={(value) => {
                          if (value) {
                            setFieldValue("state", value.value);
                          }
                        }}
                      />
                    </FormField>
                  </div>
                  <div className="flex">
                    <FormField
                      className="w-6/12 mr-1"
                      id="zipCode"
                      label="Zip Code"
                      error={errors.zipCode}
                    >
                      <TextInput
                        id="zipCode"
                        name="zipCode"
                        value={values.zipCode}
                        onChange={(e) => {
                          setFieldValue("zipCode", e.target.value);
                        }}
                      />
                    </FormField>
                    <FormField
                      id="country"
                      label="Country"
                      className="w-6/12 ml-1"
                    >
                      <TextInput
                        id="country"
                        name="country"
                        value={values.country}
                        disabled
                      />
                    </FormField>
                  </div>

                  <FormField
                    id="phoneNumber"
                    label="Phone Number"
                    error={errors.phoneNumber}
                  >
                    <TextInput
                      id="phoneNumber"
                      name="phoneNumber"
                      value={values.phoneNumber}
                      onChange={(e) => {
                        setFieldValue("phoneNumber", e.target.value);
                      }}
                    />
                  </FormField>
                  <Button type="submit">Save</Button>
                </Form>
              )}
            </Formik>
          );
        }}
      </QueryComponent>
    </div>
  );
};

export default Contact;
